// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — AI Agent Page
// ═══════════════════════════════════════════════════════════

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { cn, formatDate } from '@/lib/utils';
import {
  Card,
  Badge,
  PageHeader,
  Button,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import {
  ArrowLeft,
  Sparkles,
  Wand2,
  Code,
  Copy,
  Check,
  Database,
  Wrench,
  History,
  Zap,
  Bot,
} from 'lucide-react';
import toast from 'react-hot-toast';

type TabId = 'generate' | 'seed' | 'fix' | 'history';

export default function AIAgentPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = parseInt(id!);

  const [activeTab, setActiveTab] = useState<TabId>('generate');
  const [description, setDescription] = useState('');
  const [roleName, setRoleName] = useState('');
  const [testType, setTestType] = useState('e2e');
  const [generatedCode, setGeneratedCode] = useState('');
  const [copied, setCopied] = useState(false);

  // Fix test state
  const [fixTestName, setFixTestName] = useState('');
  const [fixErrorMessage, setFixErrorMessage] = useState('');
  const [fixResult, setFixResult] = useState('');

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
  });

  const { data: recommendedModel } = useQuery({
    queryKey: ['recommended-model'],
    queryFn: () => api.getRecommendedModel('gemini'),
  });

  const { data: history } = useQuery({
    queryKey: ['ai-history', projectId],
    queryFn: () => api.aiHistory(projectId),
    enabled: activeTab === 'history',
  });

  const generateTest = useMutation({
    mutationFn: () => api.generateTest({ projectId, description, roleName, testType }),
    onSuccess: (data) => {
      setGeneratedCode(data.code || data.generationOutput || '');
      toast.success(t('ai.generatedWith', { model: data.model || data.generationModel }));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const generateSeed = useMutation({
    mutationFn: () => api.generateSeed({ projectId, description }),
    onSuccess: (data) => {
      setGeneratedCode(data.code || data.generationOutput || '');
      toast.success(t('ai.seedGenerated'));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const fixTest = useMutation({
    mutationFn: () =>
      api.fixTest({ projectId, testName: fixTestName, errorMessage: fixErrorMessage }),
    onSuccess: (data) => {
      setFixResult(data.fix);
      toast.success(t('ai.fixGenerated'));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedCode || fixResult);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'generate', label: t('ai.tabGenerate'), icon: <Wand2 className="w-4 h-4" /> },
    { id: 'seed', label: t('ai.tabSeed'), icon: <Database className="w-4 h-4" /> },
    { id: 'fix', label: t('ai.tabFix'), icon: <Wrench className="w-4 h-4" /> },
    { id: 'history', label: t('ai.tabHistory'), icon: <History className="w-4 h-4" /> },
  ];

  return (
    <div className="p-8">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(`/projects/${projectId}`)}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.backTo', { name: project?.projectName || t('nav.projects') })}
      </Button>

      <PageHeader
        title={t('ai.title')}
        subtitle={t('ai.subtitle', { model: recommendedModel?.displayName || 'AI' })}
        actions={
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center animate-pulse-glow">
              <Bot className="w-5 h-5 text-white" />
            </div>
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-surface-900/80 rounded-xl w-fit mb-8">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
            className={cn(activeTab === tab.id ? 'shadow-lg shadow-brand-600/25' : '')}
          >
            {tab.icon}
            {tab.label}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left — Input */}
        <Card>
          {activeTab === 'generate' && (
            <div className="space-y-5">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-brand-400" />
                <h3 className="text-lg font-semibold text-surface-100">
                  {t('ai.generatePlaywright')}
                </h3>
              </div>
              <Textarea
                label={t('ai.describeTest')}
                rows={6}
                placeholder={t('ai.testDescPlaceholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Input
                label={t('ai.roleName')}
                placeholder={t('ai.roleNamePlaceholder')}
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
              />
              <div className="flex gap-3">
                <Select value={testType} onValueChange={setTestType}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder={t('ai.testType')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="e2e">{t('ai.testTypeE2E')}</SelectItem>
                    <SelectItem value="auth">{t('ai.testTypeAuth')}</SelectItem>
                    <SelectItem value="visual">{t('ai.testTypeVisual')}</SelectItem>
                    <SelectItem value="api">{t('ai.testTypeAPI')}</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  loading={generateTest.isPending}
                  onClick={() => generateTest.mutate()}
                  disabled={!description}
                >
                  <Zap className="w-4 h-4" />
                  {t('ai.generate')}
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'seed' && (
            <div className="space-y-5">
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-5 h-5 text-brand-400" />
                <h3 className="text-lg font-semibold text-surface-100">
                  {t('ai.generateSeedTitle')}
                </h3>
              </div>
              <Textarea
                label={t('ai.seedDescLabel')}
                rows={6}
                placeholder={t('ai.seedDescPlaceholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Button
                loading={generateSeed.isPending}
                onClick={() => generateSeed.mutate()}
                disabled={!description}
              >
                <Zap className="w-4 h-4" />
                {t('ai.generateSeed')}
              </Button>
            </div>
          )}

          {activeTab === 'fix' && (
            <div className="space-y-5">
              <div className="flex items-center gap-2 mb-4">
                <Wrench className="w-5 h-5 text-brand-400" />
                <h3 className="text-lg font-semibold text-surface-100">{t('ai.fixTitle')}</h3>
              </div>
              <Input
                label={t('ai.testName')}
                placeholder={t('ai.testNamePlaceholder')}
                value={fixTestName}
                onChange={(e) => setFixTestName(e.target.value)}
              />
              <Textarea
                label={t('ai.errorMessage')}
                rows={6}
                placeholder={t('ai.errorPlaceholder')}
                value={fixErrorMessage}
                onChange={(e) => setFixErrorMessage(e.target.value)}
              />
              <Button
                loading={fixTest.isPending}
                onClick={() => fixTest.mutate()}
                disabled={!fixTestName || !fixErrorMessage}
              >
                <Wrench className="w-4 h-4" />
                {t('ai.suggestFix')}
              </Button>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-brand-400" />
                {t('ai.historyTitle')}
              </h3>
              {history && history.length > 0 ? (
                history.map((gen) => (
                  <div
                    key={gen.generationId}
                    className="p-3 rounded-lg bg-surface-800/30 cursor-pointer hover:bg-surface-800/50 transition-colors"
                    onClick={() => setGeneratedCode(gen.generationOutput || '')}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant={gen.generationStatus === 'completed' ? 'success' : 'danger'}>
                        {gen.generationType}
                      </Badge>
                      <span className="text-xs text-surface-500">
                        {gen.generationCreatedAt ? formatDate(gen.generationCreatedAt) : ''}
                      </span>
                    </div>
                    <p className="text-sm text-surface-300 line-clamp-2">{gen.generationInput}</p>
                    <p className="text-xs text-surface-500 mt-1">
                      {t('ai.model', { model: gen.generationModel })}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-surface-500 text-center py-6">{t('ai.noHistory')}</p>
              )}
            </div>
          )}
        </Card>

        {/* Right — Output */}
        <Card className="relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Code className="w-5 h-5 text-brand-400" />
              <h3 className="text-lg font-semibold text-surface-100">{t('ai.generatedOutput')}</h3>
            </div>
            {(generatedCode || fixResult) && (
              <Button variant="ghost" size="sm" onClick={handleCopy}>
                {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                {copied ? t('common.copied') : t('common.copy')}
              </Button>
            )}
          </div>

          {generatedCode || fixResult ? (
            <pre className="p-4 rounded-lg bg-surface-950 border border-surface-800 overflow-auto max-h-[600px] text-sm font-mono text-surface-300 whitespace-pre-wrap">
              {generatedCode || fixResult}
            </pre>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-surface-800/50 flex items-center justify-center text-surface-500 mb-4">
                <Code className="w-8 h-8" />
              </div>
              <p className="text-sm text-surface-500">{t('ai.outputPlaceholder')}</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
